import * as React from 'react';

import TouchRipple from "material-ui/internal/TouchRipple";
import { autoprefixer } from "../../material-ui/autoprefixer";

import colors from './colors';

export interface IconTextButtonProps extends React.Props<IconTextButton> {
    Icon: React.ReactElement<__MaterialUI.SvgIconProps>;
    AdditionalIcon?: typeof __MaterialUI.SvgIcon;
    text: string|React.ReactNode;
    style?: React.CSSProperties;
    buttonStyle?: React.CSSProperties;
    isDisabled?: boolean;
    isPressable?: boolean;
    isPressed?: boolean;
    useAlternativeColors?: boolean;
    useLightColors?: boolean;
    dataTestId?: string;
    isPrimary?: boolean;
    isSecondary?: boolean;
    isLabelWrapped?: boolean;
    onTouchTap?(event: React.SyntheticEvent<{}>, isPressed?: boolean): void;
}

interface IconTextButtonState {
    isActive: boolean;
    isHover: boolean;
    isPressed: boolean;
}

export default class IconTextButton extends React.Component<IconTextButtonProps, IconTextButtonState> {

    private isTouched: boolean;

    constructor(props: IconTextButtonProps) {
        super(props);
        this.state = {
            isActive: false,
            isHover: false,
            isPressed: props.isPressed || false
        };
        this.isTouched = false;
    }

    componentWillReceiveProps(nextProps: IconTextButtonProps) {
        if (nextProps.isDisabled !== this.props.isDisabled) {
            this.setState({
                isActive: false,
                isHover: false
            } as IconTextButtonState);
        }
    }

    render() {
        const { Icon, AdditionalIcon } = this.props;
        const styles = this.getStyles();
        const colorsRoot = this.props.useLightColors ?
            colors.light() :
            this.props.useAlternativeColors
            ? colors.alternative(this.props.isPrimary)
            : colors.default(this.props.isPrimary, this.props.isSecondary);

        const iconColor = (() => {
            if (this.props.isDisabled) {
                return colorsRoot.iconColorDisabled;
            }
            if (!this.props.AdditionalIcon) {
                if (this.props.isPressable ? this.state.isPressed : this.props.isPressed) {
                    return colorsRoot.iconColorPressed;
                }
            }
            return colorsRoot.iconColor;
        })();

        const icon = React.cloneElement(Icon, {
            color: iconColor,
            style: Object.assign({}, styles.icon, Icon.props.style),
            key: 0
        } as __MaterialUI.SvgIconProps);

        const content = (
            <div style={styles.content}>
                {
                    AdditionalIcon
                        ? (
                        <div style={styles.iconWrapper}>
                            {icon}
                            <AdditionalIcon color={iconColor} style={styles.additionalIcon} />
                        </div>
                    )
                        : icon
                }
                <div style={styles.text}>{this.props.text}</div>
            </div>
        );

        return (
            <div
                style={Object.assign({}, styles.root, this.props.style)}
                data-test-id={this.props.dataTestId}
                onTouchTap={this.onTouchTap}
                onMouseDown={this.onMouseDown}
                onMouseUp={this.onMouseUp}
                onMouseOver={this.onMouseOver}
                onMouseOut={this.onMouseOut}
                onTouchStart={() => this.isTouched = true}
            >
                <button style={Object.assign({}, styles.button, this.props.buttonStyle)}>
                    {
                        this.props.isDisabled ?
                            content :
                            <TouchRipple color={colorsRoot.rippleColor} style={styles.ripple}>
                                {content}
                            </TouchRipple>
                    }
                </button>
            </div>
        );
    }

    private onTouchTap = (event: React.SyntheticEvent<{}>) => {
        // prevent ghost click events on touch devices
        event.preventDefault();
        if (this.props.isDisabled) {
            return;
        }
        if (this.props.isPressable) {
            const newState = {} as IconTextButtonState;
            newState.isPressed = !this.state.isPressed;
            this.setState(newState);
            if (this.props.onTouchTap) {
                this.props.onTouchTap(event, newState.isPressed);
            }
        } else {
            if (this.props.onTouchTap) {
                this.props.onTouchTap(event);
            }
        }
    };

    private onMouseDown = (event: React.SyntheticEvent<{}>) => {
        if (this.props.isDisabled) {
            return;
        }
        if (!this.state.isActive) {
            const newState = {} as IconTextButtonState;
            newState.isActive = true;
            this.setState(newState);
        }
    };

    private onMouseUp = (event: React.SyntheticEvent<{}>) => {
        if (this.props.isDisabled) {
            return;
        }
        if (this.state.isActive) {
            const newState = {} as IconTextButtonState;
            newState.isActive = false;
            this.setState(newState);
        }
    };

    private onMouseOver = (event: React.SyntheticEvent<{}>) => {
        if (this.props.isDisabled) {
            return;
        }
        if (!this.isTouched && !this.state.isHover) {
            const newState = {} as IconTextButtonState;
            newState.isHover = true;
            this.setState(newState);
        }
    };

    private onMouseOut = (event: React.SyntheticEvent<{}>) => {
        if (this.props.isDisabled) {
            return;
        }
        if (this.state.isActive || this.state.isHover) {
            const newState = {} as IconTextButtonState;
            newState.isActive = false;
            newState.isHover = false;
            this.setState(newState);
        }
    };

    private getStyles() {
        const buttonHeight = 48;
        const colorsRoot = this.props.useLightColors ?
            colors.light() :
            this.props.useAlternativeColors
            ? colors.alternative(this.props.isPrimary)
            : colors.default(this.props.isPrimary, this.props.isSecondary);

        const transitionType = 'cubic-bezier(0.62, 0.28, 0.23, 0.99) 0.3s';

        const textColor = (() => {
            if (this.props.isDisabled) {
                return colorsRoot.textColorDisabled;
            }
            if (!this.props.AdditionalIcon) {
                if (this.props.isPressable ? this.state.isPressed : this.props.isPressed) {
                    return colorsRoot.textColorPressed;
                }
            }
            return colorsRoot.textColor;
        })();

        const backgroundColor = (() => {
            if (this.props.isPressable ? this.state.isPressed : this.props.isPressed) {
                if (this.props.AdditionalIcon) {
                    return colorsRoot.backgroundColorActive;
                }
                if (this.props.isDisabled) {
                    return colorsRoot.backgroundColorPressedDisabled;
                }
                return colorsRoot.backgroundColorPressed;
            }
            if (this.state.isActive) {
                return colorsRoot.backgroundColorActive;
            }
            if (this.state.isHover) {
                return colorsRoot.backgroundColorHover;
            }
            return colorsRoot.backgroundColor;
        })();

        const shadowColor = (() => {
            if (this.props.isDisabled) {
                return colorsRoot.shadowColorDisabled;
            }
            return colorsRoot.shadowColor;
        })();

        const pressed = this.props.isPressable ? this.state.isPressed : this.props.isPressed;
        const isLabelWrapped = this.props.isLabelWrapped;

        return {
            root: autoprefixer({
                display: 'inline-block',
                userSelect: 'none'
            }),
            button: Object.assign({
                display: 'block',
                margin: 0,
                border: 0,
                outline: 0,
                padding: 0,
                backgroundColor,
                position: 'relative',
                cursor: this.props.isDisabled ? 'not-allowed' : 'pointer',
                borderRadius: 3,
                overflow: 'hidden',
                zIndex: 10,
                transition: `background-color ${transitionType}, box-shadow ${transitionType}`,
            }, (!this.props.AdditionalIcon && pressed)
              ? { boxShadow: `inset 0 0 1px ${shadowColor}, inset 0 0 7px ${shadowColor}` }
              : {}
            ),
            ripple: {
                pointerEvents: 'none'
            },
            content: autoprefixer({
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: isLabelWrapped ? 'flex-start' : 'center',
                // maybe switching minHeight/height/maxHeight between the flag is not necessary
                minHeight: isLabelWrapped ? buttonHeight : '',
                maxHeight: isLabelWrapped ? '' : buttonHeight,
                height: isLabelWrapped ? '' : buttonHeight,
                pointerEvents: 'none',
                marginLeft: 12,
                marginRight: 12
            }),
            icon: {},
            iconWrapper: autoprefixer({
                lineHeight: '1px',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center'
            }),
            additionalIcon: {
                height: 16,
                width: 16,
                marginLeft: 4,
                marginRight: -2
            },
            text: {
                color: textColor,
                fontFamily: '"Open Sans", sans-serif',
                fontSize: 12,
                maxWidth: (isLabelWrapped ? 100 : 200) - 2 * 12,
                overflow: 'hidden',
                textOverflow: isLabelWrapped ? '' : 'ellipsis',
                whiteSpace: isLabelWrapped ? 'normal' : 'nowrap',
                fontWeight: 600,
                paddingTop: 1
            }
        };
    }
}



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/custom-components/icon-text-button/IconTextButton.tsx
 **/